/**
 * Модалка со списком дел — режим `openList()` макета (ui-blocks.md §10).
 *
 * Открывается кликом по KPI-плитке, ячейке календаря, строке нагрузки или
 * категории; данные — `GET /api/dashboard/list`. Клик по строке открывает
 * карточку дела поверх выборки (`useOpenCase` меняет адрес на `/cases/:id`,
 * экран со списком остаётся под окном), закрытие карточки возвращает сюда.
 *
 * Каркас окна, фокус-ловушка и `inert` — общий `Modal` каркаса.
 */

import type { KeyboardEvent } from 'react';
import { Modal, useOpenCase } from '@/components/Modal';
import { useCaseList } from './api';
import { PriorityBadge } from './Delta';
import { caseWord, deadlineClass } from './format';
import type { CaseListItem, ListFilters, ListKpi, PeriodValue } from './types';
import { LoadingState } from '../ErrorState';

interface Props {
    title: string;
    kpi: ListKpi;
    params?: ListFilters;
    period: PeriodValue;
    onClose: () => void;
}

export function CaseListModal({ title, kpi, params, period, onClose }: Props) {
    const list = useCaseList(kpi, params, period);
    const openCase = useOpenCase();

    // Сортировка макета: выборка всегда идёт по сроку ответа.
    const items: CaseListItem[] = [...(list.data?.items ?? [])].sort((a, b) =>
        (a.legal_deadline ?? '').localeCompare(b.legal_deadline ?? ''),
    );
    const total = list.data?.total ?? items.length;

    const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, id: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openCase(id);
    };

    return (
        <Modal
            title={title}
            idLine="Выборка дел"
            chips={
                <span>
                    {total} {caseWord(total)}
                </span>
            }
            onClose={onClose}
        >
            {list.isLoading && <LoadingState label="Загружаем выборку…" />}
            {list.isError && <div className="list-empty">Не удалось загрузить выборку.</div>}
            {!list.isLoading && !list.isError && items.length === 0 && (
                <div className="list-empty">В выборке нет дел.</div>
            )}
            {items.map((item) => (
                <div
                    key={item.case_id}
                    className="list-row"
                    data-case={item.case_id}
                    data-inlist="1"
                    role="button"
                    tabIndex={0}
                    onClick={() => openCase(item.case_id)}
                    onKeyDown={(event) => onRowKeyDown(event, item.case_id)}
                >
                    <span className="lr-id">{item.number}</span>
                    <PriorityBadge priority={item.priority} />
                    <span className="lr-title">
                        {item.title}
                        {item.escalation_risk ? ' 🛡' : ''}
                        {item.extended ? ' ⏳' : ''}
                    </span>
                    <span className="lr-exec">{item.executor_name ?? '—'}</span>
                    <span className={`lr-date ${deadlineClass(item.deadline_state)}`.trim()}>
                        {item.deadline_text}
                    </span>
                </div>
            ))}
        </Modal>
    );
}
